import { useState } from "react";
import { PaperClipIcon, TrashIcon } from "@heroicons/react/24/outline";

export default function StudentGradesTable({ data, type = "editable", onUpdate,permission }) {
  const [rows, setRows] = useState(
    data.map((row) => ({
      ...row,
      date: row.date || null,
      updatedDate: row.updatedDate || null,
    }))
  );
  const [editingIndex, setEditingIndex] = useState(null);
  const [selected, setSelected] = useState([]); // store selected ids

  // handle checkbox toggle
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  // handle select all
  const toggleSelectAll = () => {
    if (selected.length === rows.length) {
      setSelected([]); // unselect all
    } else {
      setSelected(rows.map((r) => r.id)); // select all
    }
  };

  // delete selected
  const handleDelete = () => {
    const updated = rows.filter((row) => !selected.includes(row.id));
    setRows(updated);
    setSelected([]);
  };

  const handleNoteChange = (index, value) => {
    const updated = [...rows];
    updated[index].notes = value;
    setRows(updated);
  };

  const handleAttachmentChange = (index, file) => {
    if (!file) return;
    const updated = [...rows];
    updated[index].notes = file.name;
    updated[index].attachment = {
      name: file.name,
      url: URL.createObjectURL(file),
    };
    setRows(updated);
  };

  const handleSave = (index) => {
    const updated = [...rows];
    const now = new Date().toLocaleString();

    if (type === "save-only") {
      if (!updated[index].date) {
        updated[index].date = now;
      }
    }

    if (type === "edit-save") {
      updated[index].updatedDate = now;
    }

    setRows(updated);
    if (onUpdate) onUpdate(index, updated[index]);
    setEditingIndex(null);
  };


  // inside component
  const handleGradeChange = (index, value) => {
    const updated = [...rows];
    updated[index].grade = value;
    setRows(updated);
  };
  return (
    <div className="overflow-hidden border border-gray-100 rounded-md mb-6">
      {/* Top bar with Delete button */}
      {permission?.deleteMultipal === true && selected.length > 0 && (
        <div className="flex justify-end p-2 bg-gray-50 border-b">
          <button
            onClick={handleDelete}
            className="flex items-center gap-1 text-red-600 hover:text-red-800"
          >
            <TrashIcon className="w-5 h-5" /> Delete
          </button>
        </div>
      )}

      <table className="w-full min-w-max text-left border-collapse">
        <thead className=" text-gray-700 text-sm py-5">
          <tr>
            <th className="py-4 px-3">
              <input
                type="checkbox"
                checked={selected.length === rows.length && rows.length > 0}
                onChange={toggleSelectAll}
                className="w-5 h-5"
              />
            </th>
            <th className="py-4 px-3">STUDENT NAME</th>
            <th className="py-4 px-3">APPLICATION</th>
            <th className="py-4 px-3">GRADE</th>
            {type === "save-only" && <th className="py-4 px-3">DATE</th>}
            {type === "edit-save" && <th className="py-4 px-3">UPDATED DATE</th>}
            <th className="py-4 px-3">NOTES</th>
            {(type === "save-only" && permission?.singleSave === true) && <th className="py-4 px-3">ACTION</th>}
          </tr>
        </thead>

        <tbody className="text-sm text-gray-600">
          {rows.map((row, index) => (
            <tr
              key={row.id}
              className="border-t hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
            >
              {/* Checkbox */}
              <td className="py-4 px-3">
                <input
                  type="checkbox"
                  checked={selected.includes(row.id)}
                  onChange={() => toggleSelect(row.id)}
                  className="w-5 h-5"
                />
              </td>

              {/* Student Info */}
              <td className="py-4 px-3 flex items-center gap-2">
                <img
                  src={row.avatar}
                  alt={row.name}
                  className="w-8 h-8 rounded-full"
                />
                <div>
                  <p className="font-medium">{row.name}</p>
                  <p className="text-xs text-gray-400">ID: {row.id}</p>
                </div>
              </td>

              <td className="py-4 px-3"> <span className="px-4 py-3 bg-[#FFA21D1A] text-[#FFA21D]">{row.application}</span> </td>
              <td className="py-4 px-3">
                <div className="relative max-w-[80px]">
                  <select
                    value={row.grade || ""}
                    onChange={(e) => handleGradeChange(index, e.target.value)}
                    className="max-w-[100px] border border-gray-200 rounded-md px-3 py-2 pr-8 text-sm 
                 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 
                 appearance-none"
                  >
                    <option value="">Select Grade</option>
                    <option value="A+">A+</option>
                    <option value="A">A</option>
                    <option value="B+">B+</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                    <option value="F">F</option>
                  </select>

                  {/* Custom select icon */}
                  <span className="absolute  top-1/2 left-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 15.5L5 8.5H19L12 15.5Z" fill="black" />
                    </svg>
                  </span>
                </div>
              </td>

              {type === "save-only" && (
                <td className="py-4 px-3">{row.date ? row.date : "-"}</td>
              )}
              {type === "edit-save" && (
                <td className="py-4 px-3">
                  {row.updatedDate ? row.updatedDate : "-"}
                </td>
              )}

              {/* Notes + Attachment */}
              <td className="py-4 px-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Add notes here"
                    value={row.notes || ""}
                    onChange={(e) => handleNoteChange(index, e.target.value)}
                    className="w-full border border-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 pr-8"
                    disabled={
                      type === "readonly" ||
                      (type === "edit-save" && editingIndex !== index)
                    }
                  />
                  <label
                    htmlFor={`file-${index}`}
                    className="absolute right-2 top-2.5 cursor-pointer"
                  >
                    <PaperClipIcon
                      className={`w-5 h-5 ${row.attachment ? "text-blue-600" : "text-gray-400"
                        }`}
                    />
                  </label>
                  <input
                    type="file"
                    id={`file-${index}`}
                    className="hidden"
                    onChange={(e) =>
                      handleAttachmentChange(index, e.target.files[0])
                    }
                    disabled={
                      type === "readonly" ||
                      (type === "edit-save" && editingIndex !== index)
                    }
                  />
                </div>

                {row.attachment && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                    <PaperClipIcon className="w-4 h-4 text-gray-400" />
                    {row.attachment.name}
                  </div>
                )}
              </td>

              {/* Actions */}
              <td className="py-4 px-3">
                {type === "save-only" && permission?.singleSave === true && (
                  <button
                    onClick={() => handleSave(index)}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Save
                  </button>
                )}

                {/* {type === "edit-save" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingIndex(index)}
                      className="px-3 py-1 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleSave(index)}
                      className="px-3 py-1 text-sm bg-green-500 text-white rounded-md hover:bg-green-600"
                    >
                      Save
                    </button>
                  </div>
                )} */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
